import {
  getAttachmentsList,
  getCurrentProjectScope,
  getFieldIssueTypesAndSubTypesIdToTitleMap,
  getIssueAttributesById,
  getProjectId,
  getThumbnails,
  getUserNameWithCompanyGetter
} from '../selectors';
import {DM_URN_TYPE, OSS_URN_TYPE} from '../../constants/attachments';
import {fetchDocumentAttachmentBlob, fetchImageAttachmentBlob, isImageAttachment} from '../../utils/attachmentsHelper';
import {getToken} from '../../utils/getToken';
import lodashUniq from 'lodash/uniq';
import groupBy from 'lodash/groupBy';
import {ENV_CONFIGS} from '../../constants/envs';
import {openIframe, showNotificationAction, trackEvent} from './index';
import i18n from '../../utils/i18n';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager';
import Text from '@adsk/bim360-matrix-react-text';
import React from 'react';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';
import {attachmentTypes} from '../../constants/analytics';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {addAttachment} from '../../utils/analytics-helpers';
import {uploadAttachment} from '../../utils/data-transfer-helpers';

export const CREATE_ATTACHMENT_FOR_CREATE = 'CREATE_ATTACHMENT_FOR_CREATE';
export const CREATE_ATTACHMENT_FROM_DOCUMENT_START = 'CREATE_ATTACHMENT_FROM_DOCUMENT_START';
export const UPLOAD_ATTACHMENT_FILE_START = 'UPLOAD_ATTACHMENT_FILE_START';
export const ATTACHMENT_UPLOAD_FINISHED = 'ATTACHMENT_UPLOAD_FINISHED';
export const ATTACHMENT_UPLOAD_FAILED = 'ATTACHMENT_UPLOAD_FAILED';
export const CANCEL_ATTACHMENT_UPLOAD = 'CANCEL_ATTACHMENT_UPLOAD';
export const ATTACHMENT_CREATE_API_FULFILLED = 'ATTACHMENT_CREATE_API_FULFILLED';
export const ATTACHMENT_CREATE_API_REJECTED = 'ATTACHMENT_CREATE_API_REJECTED';
export const DELETE_ATTACHMENT = 'DELETE_ATTACHMENT';
export const ATTACHMENT_DELETE_API_FULFILLED = 'ATTACHMENT_DELETE_API_FULFILLED';
export const ATTACHMENT_DELETE_API_REJECTED = 'ATTACHMENT_DELETE_API_REJECTED';
export const OPEN_ATTACHMENT = 'OPEN_ATTACHMENT';
export const FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS = 'FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS';
export const DOCUMENT_THUMBNAIL_URL = 'DOCUMENT_THUMBNAIL_URL';
export const DOCUMENT_THUMBNAIL_URL_START = 'DOCUMENT_THUMBNAIL_URL_START';
export const SET_THUMBNAIL_URL = 'SET_THUMBNAIL_URL';
export const CLEAR_ALL_THUMBNAILS = 'CLEAR_ALL_THUMBNAILS';
export const UPDATE_UPLOAD_ATTACHMENTS_PROGRESS = 'UPDATE_UPLOAD_ATTACHMENTS_PROGRESS';
export const CLEAR_FAILED_UPLOADING_ATTACHMENTS = 'CLEAR_FAILED_UPLOADING_ATTACHMENTS';
export const CLEAR_UPLOADING_ATTACHMENTS_BY_ORIGIN = 'CLEAR_UPLOADING_ATTACHMENTS_BY_ORIGIN';
export const SHOW_ATTACHMENTS_IN_GALLERY = 'SHOW_ATTACHMENTS_IN_GALLERY';
export const CLOSE_GALLERY = 'CLOSE_GALLERY';

export const fetchProjectFolders = (resolve, reject) => (dispatch, getState, {docsSDK}) => {
  docsSDK
    .getFoldersListByProjectId(getProjectId(getState()))
    .then(values => {
      resolve(sortAlphabeticallyByPath(values, 'title'));
    })
    .catch(reject);
};

export const fetchFolderContents = (urn, options, resolve, reject) => (dispatch, getState, {docsSDK}) => {
  const state = getState();
  docsSDK
    .getFolderContents(getProjectId(state), urn, options)
    .then(values => {
      values.documents = sortAlphabeticallyByPath(values.documents, 'name');
      values.folders = sortAlphabeticallyByPath(values.folders, 'title');
      resolve(values);
    })
    .catch(reject);
};

export const createAttachmentForCreate = attachment => ({
  type: CREATE_ATTACHMENT_FOR_CREATE,
  payload: {
    attachment
  }
});

const setAttachmentThumbnailAction = blob => dispatch => {
  if (blob?.blob) {
    dispatch({
      type: SET_THUMBNAIL_URL,
      payload: {
        attachmentUrn: blob.urn,
        attachmentUrl: URL.createObjectURL(blob.blob)
      }
    });
  }
};

const fetchImageAttachmentThumbnailsAction = ossAttachments => (dispatch, getState) => {
  const tokenPromise = getToken({dispatch, getState});
  const projectScope = getCurrentProjectScope(getState());
  ossAttachments.filter(isImageAttachment).forEach(attachment => {
    fetchImageAttachmentBlob({attachment, tokenPromise, projectScope}).then(blob =>
      dispatch(setAttachmentThumbnailAction(blob))
    );
  });
};

const fetchDocumentAttachmentDocuments = (attachments, docsSDK, projectId) => {
  // Get blob for linked dm attachments
  const documentUrnsToFetch = lodashUniq(attachments.map(attachment => attachment.urn));

  // Fetch documents data by urns so we can fetch their thumbnail using their bubble_urn
  return (documentUrnsToFetch.length > 0
    ? docsSDK.getDocumentsByURNs(projectId, documentUrnsToFetch)
    : Promise.resolve([])
  ).then(documents => Object.fromEntries(documents.map(doc => [doc.urn, doc])));
};

const fetchDocumentAttachmentThumbnailsAction = dmAttachments => (dispatch, getState, {docsSDK}) => {
  const tokenPromise = getToken({dispatch, getState});
  const state = getState();
  const projectId = getProjectId(state);
  const projectScope = getCurrentProjectScope(state);
  const documentsByUrnsPromise = fetchDocumentAttachmentDocuments(dmAttachments, docsSDK, projectId);

  dmAttachments.forEach(attachment => {
    fetchDocumentAttachmentBlob({attachment, tokenPromise, documentsByUrnsPromise, projectScope}).then(blob =>
      dispatch(setAttachmentThumbnailAction(blob))
    );
  });
};

export const fetchAttachmentThumbnailsAction = attachments => (dispatch, getState) => {
  const state = getState();
  const existingThumbnails = getThumbnails(state);

  const attachmentsByUrnType = groupBy(
    attachments.filter(attachment => !existingThumbnails[attachment.urn]),
    'urnType'
  );

  dispatch(fetchImageAttachmentThumbnailsAction(attachmentsByUrnType[OSS_URN_TYPE] ?? []));
  dispatch(fetchDocumentAttachmentThumbnailsAction(attachmentsByUrnType[DM_URN_TYPE] ?? []));
};

const trackAddAttachmentEventAction = ({attachment, issueId}) => (dispatch, getState) => {
  const state = getState();
  const issue = getIssueAttributesById(state, issueId);
  const attachmentType = attachment.urnType === OSS_URN_TYPE ? attachmentTypes.UPLOAD : attachmentTypes.LINK;
  const issueTypesAndSubTypesTitleMap = getFieldIssueTypesAndSubTypesIdToTitleMap(state);
  const issueTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]];
  const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];

  dispatch(trackEvent(addAttachment(attachmentType, attachment.name, issueTypeTitle, issueSubTypeTitle)));
};

export const attachmentCreateApiFulfilled = ({attachment, fileId, issueId}) => dispatch => {
  // update attachment thumbnails
  dispatch(fetchAttachmentThumbnailsAction([attachment]));
  // send analytics event
  dispatch(trackAddAttachmentEventAction({attachment, issueId}));

  // update attachment upload status to complete and add to attachments list
  dispatch({
    type: ATTACHMENT_CREATE_API_FULFILLED,
    meta: {fileId},
    payload: attachment
  });
};

export const attachmentCreateApiRejected = ({fileId}) => ({
  type: ATTACHMENT_CREATE_API_REJECTED,
  meta: {fileId}
});

const attachmentCreateFromDocumentStartAction = ({document, issueId, origin, fileId}) => ({
  type: CREATE_ATTACHMENT_FROM_DOCUMENT_START,
  payload: {document, issueId, origin},
  meta: {id: issueId, fileId}
});

export const createAttachmentFromDocument = (document, issueId, origin) => (dispatch, getState, {issuesV2Api}) => {
  const urn = document.urn;
  const name = document.currentVersion.title;
  const attachments = getAttachmentsList(getState());
  const isExistingAttachment = attachments.findIndex(attachment => attachment.urn === urn) !== -1;
  if (isExistingAttachment) {
    dispatch(
      showNotificationAction({
        type: NotificationTypes.SNACKBAR,
        level: NotificationLevels.ERROR,
        message: i18n.t('errors.attachmentExistsError')
      })
    );
  } else {
    dispatch(attachmentCreateFromDocumentStartAction({document, issueId, origin, fileId: urn}));
    issuesV2Api
      .createAttachmentFromDocs(issueId, {name, urn})
      .then(attachment => {
        dispatch(
          attachmentCreateApiFulfilled({
            attachment,
            fileId: urn,
            issueId
          })
        );
      })
      .catch(() => {
        dispatch(attachmentCreateApiRejected({fileId: urn}));
      });
  }
};

export const uploadAttachmentFileStart = (fileId, name, issueId, origin) => ({
  type: UPLOAD_ATTACHMENT_FILE_START,
  payload: {
    fileId,
    id: fileId,
    name,
    issueId,
    origin
  }
});

export const uploadAttachmentFinished = (issueId, document, fileId, createAfterUpload, attachmentId) => ({
  type: ATTACHMENT_UPLOAD_FINISHED,
  meta: {id: issueId, fileId, createAfterUpload, attachmentId},
  payload: document
});

export const uploadAttachmentFailed = fileId => ({
  type: ATTACHMENT_UPLOAD_FAILED,
  payload: {
    fileId
  }
});

export const cancelAttachmentUpload = fileId => ({
  type: CANCEL_ATTACHMENT_UPLOAD,
  payload: {
    fileId
  }
});

export const openAttachmentAction = attachment => (dispatch, getState, {redispatcher, docsSDK}) => {
  const state = getState();
  if (redispatcher?.listenerCount(OPEN_ATTACHMENT)) {
    docsSDK.getDocumentsByURNs(getProjectId(state), [attachment.urn]).then(([result]) => {
      if (result) {
        const completeAttachmentData = {
          urn: attachment.urn,
          urnType: attachment.urnType,
          name: attachment.name,
          createdBy: attachment.createdBy,
          folderUrn: result.parentFolderUrn
        };
        dispatch({
          type: OPEN_ATTACHMENT,
          payload: completeAttachmentData
        });
      } else {
        const createdBy = getUserNameWithCompanyGetter(state)(attachment.createdBy, null);
        const attachmentReadPermissionsErrorWithUserName = i18n.t('errors.attachmentReadPermissionsErrorWithUserName', {
          userName: createdBy
        });
        const errorText = createdBy ? (
          <Text hasHTML>{attachmentReadPermissionsErrorWithUserName}</Text>
        ) : (
          i18n.t('errors.attachmentReadPermissionsError')
        );
        dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.INFO,
            message: errorText
          })
        );
      }
    });
  } else {
    const projectId = getProjectId(state);
    const {urn, name} = attachment;
    const baseUrl = `${ENV_CONFIGS.DOCS}/projects/${projectId}`;
    const urlQueryParams = `panels=default&layouts=disableTitleBar&requestDomain=${window.location.origin}`;
    const url = `${baseUrl}/viewer/items/${urn}?${urlQueryParams}`;

    dispatch(openIframe(url, name));
  }
};

export const showAttachmentsInGallery = (attachments, selectedIndex = 0) => ({
  type: SHOW_ATTACHMENTS_IN_GALLERY,
  payload: {
    selectedIndex,
    attachments
  }
});

export const closeGallery = () => ({
  type: CLOSE_GALLERY
});

export const setDocumentThumbnailUrlPromiseAction = thumbnailPromise => ({
  type: DOCUMENT_THUMBNAIL_URL,
  payload: thumbnailPromise
});

export const clearAllThumbnails = () => (dispatch, getState) => {
  const thumbnails = getThumbnails(getState());
  Object.values(thumbnails).forEach(({attachmentUrl}) => {
    URL.revokeObjectURL(attachmentUrl);
  });
  dispatch({
    type: CLEAR_ALL_THUMBNAILS
  });
};

export const updateUploadAttachmentProgress = (fileId, progress) => ({
  type: UPDATE_UPLOAD_ATTACHMENTS_PROGRESS,
  payload: {
    fileId,
    progress
  }
});

export const clearFailedUploadingAttachments = () => ({
  type: CLEAR_FAILED_UPLOADING_ATTACHMENTS
});

export const clearUploadingAttachmentsListByOrigin = origin => ({
  type: CLEAR_UPLOADING_ATTACHMENTS_BY_ORIGIN,
  payload: origin
});

export const deleteAttachmentAction = ({attachmentId, analyticsObj, issueId}) => (
  dispatch,
  getState,
  {issuesV2Api}
) => {
  dispatch({
    type: DELETE_ATTACHMENT,
    payload: {
      attachmentId,
      analyticsObj,
      issueId
    }
  });
  issuesV2Api
    .deleteAttachment(issueId, attachmentId)
    .then(() => {
      dispatch({
        type: ATTACHMENT_DELETE_API_FULFILLED,
        meta: {attachmentId, analyticsObj}
      });
    })
    .catch(() => {
      dispatch({type: ATTACHMENT_DELETE_API_REJECTED});
    });
};

export const fieldIssuesActivityFetchAttachments = attachments => ({
  type: FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS,
  payload: attachments
});

export const uploadAttachmentFiles = (fileWrappers, issueId, createAfterUpload, origin) => (
  dispatch,
  getState,
  {issuesV2Api}
) => {
  const projectScope = getCurrentProjectScope(getState());
  fileWrappers.forEach(({file, id: fileId}) => {
    const fileName = file.name;
    dispatch(uploadAttachmentFileStart(fileId, fileName, issueId, origin));
    const onUploadProgress = progressEvent => {
      dispatch(updateUploadAttachmentProgress(fileId, (100 * progressEvent.loaded) / progressEvent.total));
    };
    Promise.all([issuesV2Api.createAttachmentUrn(issueId, file.name), getToken({dispatch, getState})])
      .then(([{urn, id: attachmentId}, token]) =>
        uploadAttachment(urn, token, file, projectScope, onUploadProgress)
          .then(() => {
            dispatch(
              uploadAttachmentFinished(
                issueId,
                {
                  name: fileName,
                  urn,
                  urnType: OSS_URN_TYPE
                },
                fileId,
                createAfterUpload,
                attachmentId
              )
            );
            return issuesV2Api.finalizeAttachmentUpload(issueId, attachmentId);
          })
          .then(attachment => {
            dispatch(attachmentCreateApiFulfilled({attachment, fileId, issueId}));
          })
          .catch(() => {
            dispatch(attachmentCreateApiRejected({fileId}));
          })
      )
      .catch(() => dispatch(uploadAttachmentFailed(fileId)));
  });
};
